/* Phrase Studios — Filter sheet (v2 2026-05-19)
   Open/close + focus trap + ESC + apply-on-change + sort-on-change.
   Drives Shopify native facets via form submission to the collection URL.
   Vanilla JS, no framework. ~120 lines.
   ============================================================ */

(function () {
  'use strict';

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(panel, event) {
    if (event.key !== 'Tab') return;
    const nodes = panel.querySelectorAll(FOCUSABLE);
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      last.focus();
      event.preventDefault();
    } else if (!event.shiftKey && document.activeElement === last) {
      first.focus();
      event.preventDefault();
    }
  }

  function openSheet(sheet, trigger) {
    if (!sheet || sheet.hidden === false) return; // already open / missing -> no-op
    sheet.hidden = false;
    requestAnimationFrame(() => sheet.classList.add('is-open'));
    document.body.classList.add('phrase-filter-sheet-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    const panel = sheet.querySelector('.phrase-filter-sheet__panel');
    if (panel) panel.focus();

    sheet._returnFocusTo = trigger || null;
    sheet._keyHandler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeSheet(sheet);
      } else if (e.key === 'Tab' && panel) {
        trapFocus(panel, e);
      }
    };
    document.addEventListener('keydown', sheet._keyHandler);
  }

  function closeSheet(sheet) {
    if (!sheet) return;
    sheet.classList.remove('is-open');
    document.body.classList.remove('phrase-filter-sheet-open');
    const trigger = sheet._returnFocusTo;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (sheet._keyHandler) {
      document.removeEventListener('keydown', sheet._keyHandler);
      sheet._keyHandler = null;
    }
    // Wait for CSS transition before hiding so focus + screen-reader stay sane.
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const delay = reducedMotion ? 0 : 280;
    setTimeout(() => {
      sheet.hidden = true;
      if (trigger) trigger.focus();
    }, delay);
  }

  function init() {
    // Bind open triggers
    document.querySelectorAll('[data-phrase-filter-open]').forEach((trigger) => {
      const targetId = trigger.getAttribute('aria-controls');
      const sheet = targetId ? document.getElementById(targetId) : null;
      if (!sheet) return;
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        openSheet(sheet, trigger);
      });
    });

    // Bind close targets (scrim + close button + any element with data-phrase-filter-close)
    document.querySelectorAll('[data-phrase-filter-sheet]').forEach((sheet) => {
      sheet.querySelectorAll('[data-phrase-filter-close]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          closeSheet(sheet);
        });
      });
    });

    // Auto-submit sort form when the select changes
    document.querySelectorAll('[data-phrase-sort]').forEach((form) => {
      const select = form.querySelector('select');
      if (!select) return;
      select.addEventListener('change', () => {
        // Carry over existing query params except sort_by + page
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });

    // View toggle (Product / Model) — emits an event but does not change images.
    // Theme image-swap wiring lives in product card layer.
    document.querySelectorAll('.phrase-coll-view').forEach((group) => {
      const btns = group.querySelectorAll('[data-phrase-view]');
      btns.forEach((btn) => {
        btn.addEventListener('click', () => {
          btns.forEach((b) => b.setAttribute('aria-pressed', 'false'));
          btn.setAttribute('aria-pressed', 'true');
          document.dispatchEvent(new CustomEvent('phrase:view-change', { detail: { view: btn.dataset.phraseView } }));
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
